import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Upload, Brain, FileSpreadsheet, 
  BarChart3, MessageSquare, CreditCard, Users, Shield, Sparkles, FilePlus, Send,
  Building2, Settings, Calculator
} from 'lucide-react';

const FEATURES = [
  {
    id: 'welcome',
    title: 'Welcome to Expense Flow!',
    subtitle: 'Your AI-powered expense management assistant',
    description: 'Let us show you how to get the most out of your new expense tracking tool.',
    icon: Sparkles,
    color: 'from-indigo-500 to-purple-600',
    highlights: []
  },
  {
    id: 'upload',
    title: 'Upload Invoices',
    subtitle: 'Drag & drop or click to upload',
    description: 'Simply drop your invoice PDFs, photos, or screenshots. We support PDF, JPG, PNG, HEIC and more.',
    icon: Upload,
    color: 'from-blue-500 to-cyan-500',
    highlights: [
      { text: 'Drop zone', position: 'top' },
      { text: 'Bulk upload for multiple files', position: 'right' }
    ],
    mockup: (
      <div className="relative bg-slate-100 dark:bg-slate-800 rounded-xl p-6 border-2 border-dashed border-slate-300 dark:border-slate-600">
        <div className="text-center">
          <Upload className="w-12 h-12 mx-auto text-indigo-500 mb-3" />
          <p className="font-medium text-slate-700 dark:text-slate-300">Drop invoices here</p>
          <p className="text-sm text-slate-500">PDF, JPG, PNG, HEIC</p>
        </div>
        {/* Arrow pointing to drop zone */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-1 rounded">Drop here!</span>
          <svg className="w-6 h-6 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 14l-5-5h10l-5 5z"/>
          </svg>
        </div>
      </div>
    )
  },
  {
    id: 'ai-extract',
    title: 'AI Data Extraction',
    subtitle: 'Automatic invoice reading',
    description: 'Our AI extracts vendor name, amount, date, GST, invoice number and more - automatically!',
    icon: Brain,
    color: 'from-purple-500 to-pink-500',
    highlights: [],
    mockup: (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 space-y-3">
        <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
          <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
            <Brain className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">AI Extracted</p>
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Invoice #INV-2024-001</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded">
            <p className="text-xs text-slate-500">Vendor</p>
            <p className="font-medium text-slate-800 dark:text-white">Acme Corp</p>
          </div>
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded">
            <p className="text-xs text-slate-500">Amount</p>
            <p className="font-medium text-slate-800 dark:text-white">A$1,250.00</p>
          </div>
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded">
            <p className="text-xs text-slate-500">GST</p>
            <p className="font-medium text-slate-800 dark:text-white">A$125.00</p>
          </div>
          <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded">
            <p className="text-xs text-slate-500">Due Date</p>
            <p className="font-medium text-slate-800 dark:text-white">30 Jan 2026</p>
          </div>
        </div>
        {/* Arrow */}
        <div className="absolute -right-16 top-1/2 -translate-y-1/2 hidden sm:flex items-center">
          <svg className="w-6 h-6 text-purple-500 rotate-180" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 14l-5-5h10l-5 5z"/>
          </svg>
          <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded whitespace-nowrap">Auto-filled!</span>
        </div>
      </div>
    )
  },
  {
    id: 'export',
    title: 'Export & Reports',
    subtitle: 'Excel, PDF, Word formats',
    description: 'Download your invoices as Excel spreadsheets, PDFs, or sync to Google Drive for backup.',
    icon: FileSpreadsheet,
    color: 'from-emerald-500 to-teal-500',
    highlights: [],
    mockup: (
      <div className="flex gap-3 justify-center">
        {[
          { name: 'Excel', color: 'bg-emerald-500', icon: '📊' },
          { name: 'PDF', color: 'bg-red-500', icon: '📄' },
          { name: 'Word', color: 'bg-blue-500', icon: '📝' }
        ].map((format) => (
          <div key={format.name} className="text-center">
            <div className={`w-16 h-16 ${format.color} rounded-xl flex items-center justify-center text-2xl shadow-lg`}>
              {format.icon}
            </div>
            <p className="text-xs font-medium mt-2 text-slate-600 dark:text-slate-400">{format.name}</p>
          </div>
        ))}
      </div>
    )
  },
  {
    id: 'analytics',
    title: 'Analytics Dashboard',
    subtitle: 'Insights at a glance',
    description: 'Track spending trends, vendor analysis, budget alerts, and get AI-powered insights.',
    icon: BarChart3,
    color: 'from-amber-500 to-orange-500',
    highlights: [],
    mockup: (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-slate-800 dark:text-white">Monthly Spending</p>
          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">↓ 12%</span>
        </div>
        <div className="flex items-end gap-2 h-24">
          {[40, 65, 45, 80, 55, 70, 50].map((h, i) => (
            <div key={i} className="flex-1 bg-gradient-to-t from-amber-500 to-orange-400 rounded-t" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-400">
          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
        </div>
      </div>
    )
  },
  {
    id: 'ai-chat',
    title: 'AI Chat Assistant',
    subtitle: 'Ask anything about your expenses',
    description: '"What did I spend on office supplies?" - Get instant answers from your AI assistant.',
    icon: MessageSquare,
    color: 'from-pink-500 to-rose-500',
    highlights: [],
    mockup: (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 space-y-3">
        <div className="flex gap-2">
          <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full" />
          <div className="bg-slate-100 dark:bg-slate-700 rounded-lg rounded-tl-none px-3 py-2 text-sm">
            What are my biggest expenses this month?
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <div className="bg-indigo-500 text-white rounded-lg rounded-tr-none px-3 py-2 text-sm max-w-[200px]">
            Your top 3 expenses are: Office Supplies (A$2,450), Software (A$1,890), Travel (A$1,200)
          </div>
          <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
            <Brain className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'create-invoice',
    title: 'Create & Send Invoices',
    subtitle: 'Professional invoices in seconds',
    description: 'Generate invoices with your logo, send to customers via email, track payments, and auto-add to income when paid.',
    icon: FilePlus,
    color: 'from-teal-500 to-emerald-500',
    highlights: [],
    mockup: (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4">
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/40 rounded flex items-center justify-center">
              <FilePlus className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 dark:text-white text-sm">INV-00001</p>
              <p className="text-xs text-slate-500">Acme Corp</p>
            </div>
          </div>
          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">Paid</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-600 dark:text-slate-400">
            <span>Web Design Services</span>
            <span>$2,500</span>
          </div>
          <div className="flex justify-between text-slate-600 dark:text-slate-400">
            <span>Logo Design</span>
            <span>$500</span>
          </div>
          <div className="flex justify-between font-semibold text-slate-800 dark:text-white pt-2 border-t border-slate-100 dark:border-slate-700">
            <span>Total (inc. GST)</span>
            <span>$3,300</span>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg">
            <Send className="w-3 h-3" />
            Send
          </button>
          <button className="flex-1 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded-lg">
            Preview
          </button>
        </div>
      </div>
    )
  },
  {
    id: 'bank-transactions',
    title: 'Bank Transactions',
    subtitle: 'Import & Auto-Categorize',
    description: 'Import CSV bank statements, create rules to auto-categorize transactions, and generate BAS summaries for your accountant.',
    icon: Building2,
    color: 'from-sky-500 to-blue-600',
    highlights: [],
    mockup: (
      <div className="space-y-3">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-sky-500" />
            <span className="text-sm font-medium text-slate-800 dark:text-white">Import CSV</span>
          </div>
          <div className="flex gap-2 text-xs">
            {['CommBank', 'ANZ', 'Westpac', 'NAB'].map(bank => (
              <span key={bank} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-400">{bank}</span>
            ))}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-medium text-slate-800 dark:text-white">Auto-Categorization Rules</span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            "TELSTRA" → <span className="text-indigo-600 font-medium">Communication</span>
          </div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-2 text-center">
          <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">✓ BAS-Ready Export</p>
        </div>
      </div>
    )
  },
  {
    id: 'payroll',
    title: 'Payroll Management',
    subtitle: 'Employees, Tax & Super',
    description: 'Manage employees, calculate PAYG tax, superannuation, and create pay runs with Australian tax compliance built-in.',
    icon: Calculator,
    color: 'from-violet-500 to-indigo-600',
    highlights: [],
    mockup: (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100 dark:border-slate-700">
          <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/40 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white">John Smith</p>
            <p className="text-xs text-slate-500">Full Time • $85,000/yr</p>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Gross Pay</span>
            <span className="font-medium text-slate-800 dark:text-white">$3,269.23</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">PAYG Tax</span>
            <span className="font-medium text-red-500">-$623.00</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Super (11.5%)</span>
            <span className="font-medium text-slate-600">$375.96</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
            <span className="font-semibold text-slate-800 dark:text-white">Net Pay</span>
            <span className="font-bold text-emerald-600">$2,646.23</span>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'recurring',
    title: 'Loans & Rent Tracking',
    subtitle: 'Manage recurring payments',
    description: 'Track business loans, property rent, and other recurring expenses separately.',
    icon: CreditCard,
    color: 'from-violet-500 to-purple-500',
    highlights: [],
    mockup: (
      <div className="space-y-2">
        {[
          { name: 'Office Rent', amount: 'A$2,500/mo', type: 'Rent', color: 'bg-blue-500' },
          { name: 'Business Loan', amount: 'A$1,200/mo', type: 'Loan', color: 'bg-purple-500' }
        ].map((item) => (
          <div key={item.name} className="bg-white dark:bg-slate-800 rounded-lg p-3 flex items-center gap-3 shadow">
            <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center`}>
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800 dark:text-white">{item.name}</p>
              <p className="text-xs text-slate-500">{item.type}</p>
            </div>
            <p className="font-semibold text-slate-800 dark:text-white">{item.amount}</p>
          </div>
        ))}
      </div>
    )
  },
  {
    id: 'team',
    title: 'Team Collaboration',
    subtitle: 'Invite your team (Pro & Team plans)',
    description: 'Add team members, assign roles, and track who uploaded what with full audit logs.',
    icon: Users,
    color: 'from-cyan-500 to-blue-500',
    highlights: [],
    mockup: (
      <div className="flex -space-x-3 justify-center">
        {['bg-indigo-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500'].map((color, i) => (
          <div key={i} className={`w-12 h-12 ${color} rounded-full border-3 border-white dark:border-slate-900 flex items-center justify-center text-white font-semibold shadow-lg`}>
            {['JD', 'SK', 'MR', 'AL'][i]}
          </div>
        ))}
        <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full border-3 border-white dark:border-slate-900 flex items-center justify-center text-slate-500 font-semibold shadow-lg">
          +3
        </div>
      </div>
    )
  },
  {
    id: 'security',
    title: 'You\'re All Set!',
    subtitle: 'Secure & Private',
    description: 'Your data is encrypted and secure. Start by uploading your first invoice!',
    icon: Shield,
    color: 'from-emerald-500 to-green-500',
    highlights: [],
    mockup: (
      <div className="text-center">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-lg mb-4">
          <Shield className="w-10 h-10 text-white" />
        </div>
        <p className="text-lg font-semibold text-slate-800 dark:text-white mb-2">Ready to go!</p>
        <p className="text-sm text-slate-500">Click &quot;Get Started&quot; to upload your first invoice</p>
      </div>
    )
  }
];

function OnboardingModal({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const feature = FEATURES[currentStep];
  const Icon = feature.icon;
  const isLastStep = currentStep === FEATURES.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      localStorage.setItem('onboarding_completed', 'true');
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${feature.color} p-6 pb-12`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{feature.title}</h2>
              <p className="text-white/80 text-sm">{feature.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 -mt-6">
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-4 min-h-[180px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="w-full relative"
              >
                {feature.mockup}
              </motion.div>
            </AnimatePresence>
          </div>

          <p className="text-slate-600 dark:text-slate-400 text-sm text-center mb-6">
            {feature.description}
          </p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-6">
            {FEATURES.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep 
                    ? 'w-6 bg-indigo-600' 
                    : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                isFirstStep
                  ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={handleSkip}
              className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Skip tour
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default OnboardingModal;
